import { FunctionComponent, useEffect } from "react";
import { useLocation } from "react-router-dom";

import useLocalStorageState from "use-local-storage-state";

import { ChevronDown, ChevronRight } from "lucide-react";

import { classNames } from "@/utils/classNames";
import { SidenavItem } from "@/constants/sidenav.config";

import { isNavPathActive, NavEnrichResult } from "../Sidebar/filterNavItems";
import { SidebarItem } from "./SidebarItem";

interface SidebarGroupProps {
	item: SidenavItem;
	isSidebarOpen: boolean;
	enrich?: (item: SidenavItem) => NavEnrichResult;
}

/**
 * Expandable nav group: a toggle row with indented child links (nav-v7 design,
 * PREC-3781). Expansion is persisted per group id; a group auto-expands when
 * one of its child routes becomes active. In the collapsed (icons-only)
 * sidebar there is no group row — the children render flat, each keeping its
 * own tooltip, so everything stays one click away without flyout machinery.
 */
export const SidebarGroup: FunctionComponent<SidebarGroupProps> = ({
	item,
	isSidebarOpen,
	enrich,
}) => {
	const { pathname } = useLocation();

	const children = item.subItems ?? [];
	const groupKey = item.id ?? item.title;
	const isChildActive = children.some((child) => isNavPathActive(pathname, child));

	const [expandedGroups, setExpandedGroups] = useLocalStorageState<Record<string, boolean>>(
		"sidebarExpandedGroups",
		{ defaultValue: {} }
	);
	const isOpen = expandedGroups[groupKey] ?? true;

	// Navigating to a child of a folded group unfolds it so the active link is
	// visible; the user can still fold it again afterwards.
	useEffect(() => {
		if (isChildActive) {
			setExpandedGroups((prev) =>
				(prev ?? {})[groupKey] === false ? { ...prev, [groupKey]: true } : prev
			);
		}
	}, [pathname]);

	const renderChild = (child: SidenavItem) => (
		<SidebarItem
			key={child.path ?? child.title}
			{...child}
			{...(enrich?.(child) ?? {})}
			isSidebarOpen={isSidebarOpen}
		/>
	);

	if (!isSidebarOpen) {
		return <>{children.map(renderChild)}</>;
	}

	// Only signal the active location on the group row while it is folded —
	// unfolded, the active child link itself carries the highlight.
	const showActive = isChildActive && !isOpen;

	return (
		<div>
			<button
				aria-expanded={isOpen}
				className={classNames(
					"group relative flex items-center h-9 rounded-[9px] text-sm font-medium mb-[1px] cursor-pointer transition-colors gap-[11px] px-[10px] w-full",
					showActive
						? "bg-sidebar-item-active text-brand-light font-semibold"
						: "text-text-meta hover:bg-sidebar-item-hover hover:text-text"
				)}
				type="button"
				onClick={() => setExpandedGroups((prev) => ({ ...prev, [groupKey]: !isOpen }))}
			>
				<span
					className={classNames(
						"relative flex items-center justify-center flex-shrink-0",
						showActive ? "text-brand-light" : "text-placeholder group-hover:text-text-meta"
					)}
				>
					{item.icon}
				</span>
				<span className="flex-1 min-w-0 truncate whitespace-nowrap text-left">{item.title}</span>
				{isOpen ? (
					<ChevronDown className="w-4 h-4 flex-shrink-0 text-placeholder" strokeWidth={2} />
				) : (
					<ChevronRight className="w-4 h-4 flex-shrink-0 text-placeholder" strokeWidth={2} />
				)}
			</button>
			{isOpen && (
				<div className="ml-[21px] pl-[7px] border-l border-gray-200">
					{children.map(renderChild)}
				</div>
			)}
		</div>
	);
};
